"use client";

import {
  DOMKeyframesDefinition,
  AnimationOptions,
  ElementOrSelector,
  useAnimate,
} from "motion/react";
import { useRef, useEffect } from "react";

type MotionAnimation = [
  selector: ElementOrSelector,
  keyframes: DOMKeyframesDefinition,
  transition?: AnimationOptions | undefined,
];

type AnimationWithRepeatLimits = {
  animation: MotionAnimation | Animation[];
  playCount: number;
};

type Animation = MotionAnimation | Animation[];

const delay = (delay: number) =>
  new Promise((r) => setTimeout(r, delay * 1000));

export const useAnimationSequence = (
  animationList: AnimationWithRepeatLimits[],
  count = 1,
  initialDelay = 0,
  resetAnimationList: AnimationWithRepeatLimits[] = [],
) => {
  const mounted = useRef(true);

  const runAnimation = async (animation: Animation) => {
    if (Array.isArray(animation[0])) {
      await Promise.all(
        animation.map(async (a) => {
          await runAnimation(a as Animation);
        }),
      );
    } else {
      // Else run the single animation
      await animate(...(animation as MotionAnimation));
    }
  };

  const handleAnimate = async (
    animationList: Array<AnimationWithRepeatLimits>,
    initialDelay: number,
  ) => {
    if (initialDelay > 0) await delay(initialDelay);
    for (let i = 0; i < count; count++) {
      let totalAnimationsRemaining = 0;
      for (const limitedRepeatAnimation of animationList) {
        if (!mounted.current) return;
        if (limitedRepeatAnimation.playCount > 0) {
          await runAnimation(limitedRepeatAnimation.animation);
          limitedRepeatAnimation.playCount--;
          totalAnimationsRemaining++;
        } else if (limitedRepeatAnimation.playCount === -1) {
          await runAnimation(limitedRepeatAnimation.animation);
          totalAnimationsRemaining++;
        }
      }
      for (const cleanupAnimation of resetAnimationList) {
        await runAnimation(cleanupAnimation.animation);
      }
      if (totalAnimationsRemaining === 0) return;
    }
  };

  useEffect(() => {
    mounted.current = true;

    handleAnimate(animationList, initialDelay);

    return () => {
      mounted.current = false;
    };
  });

  const [scope, animate] = useAnimate();

  return scope;
};
